"use client"

/**
 * Guards against `NotFoundError: Failed to execute 'removeChild' on 'Node'` (and the
 * matching `insertBefore` variant) thrown from React's commit phase.
 *
 * DOM-mutating browser extensions — Google Translate above all, which Chrome auto-offers on
 * mobile when <html> has no matching lang — swap React-managed text nodes for their own <font>
 * wrappers. When React later unmounts that subtree, the native removeChild it recorded is no
 * longer a child of the expected parent, so the browser throws and React's reconciliation
 * crashes (Sentry ALDEX-CASINO-T / AC-656). The trace is entirely inside react-dom because the
 * fault is environmental, not in our code.
 *
 * The fix is the well-known React + Google Translate mitigation: make removeChild/insertBefore
 * no-op when the reference node isn't actually a child of `this`, instead of throwing. React's
 * own bookkeeping is unaffected — the node it wanted gone is already detached.
 *
 * Installed at module scope so it patches as soon as the client chunk evaluates, before any
 * React commit that could hit a translated node — earlier than a useEffect could. The typeof
 * guard makes it a no-op during SSR, where the DOM `Node` global does not exist.
 */

let installed = false;

function installTranslateCrashGuard() {
    if (installed) return;
    if (typeof Node !== "function" || !Node.prototype) return;
    installed = true;

    const originalRemoveChild = Node.prototype.removeChild;
    Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
        if (child.parentNode !== this) {
            // Extension already moved/removed the node; React's target is gone. No-op.
            return child;
        }
        return originalRemoveChild.call(this, child) as T;
    };

    const originalInsertBefore = Node.prototype.insertBefore;
    Node.prototype.insertBefore = function <T extends Node>(this: Node, newNode: T, referenceNode: Node | null): T {
        if (referenceNode && referenceNode.parentNode !== this) {
            // Reference node was relocated by the extension; append instead of throwing.
            return originalInsertBefore.call(this, newNode, null) as T;
        }
        return originalInsertBefore.call(this, newNode, referenceNode) as T;
    };
}

installTranslateCrashGuard();

export const TranslateCrashGuard = () => null;
